import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { Download } from 'lucide-react';
import AvailabilityChart from '../../components/reports/AvailabilityChart';
import AvailabilitySummaryCards from '../../components/reports/AvailabilitySummaryCards';
import ExperienceChart from '../../components/reports/ExperienceChart';
import GroupStatisticsChart from '../../components/reports/GroupStatisticsChart';
import ReportsFilters from '../../components/reports/ReportsFilters';
import SkillDistributionChart from '../../components/reports/SkillDistributionChart';
import SummaryCards from '../../components/reports/SummaryCards';
import {
  getAvailabilityReport,
  getDashboardSummary,
  getExperienceDistribution,
  getGroupStatistics,
  getSkillsDistribution,
} from '../../services/reportsService';
import { useTheme } from '../../context/ThemeContext';
import type {
  DashboardSummary,
  ReportsData,
  ReportsFiltersState,
} from '../../types/reports';
import type { AvailabilityReportItem } from '../../types/reports';

import { useMenuLabels } from '@/hooks/useMenuLabels';

const emptyFilters: ReportsFiltersState = {
  skill: '',
  groupType: '',
  availabilityType: '',
};

const freshQueryOptions = {
  staleTime: 0,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
};

type ExportRow = Record<string, string | number>;

function downloadExcel(
  sheets: Array<{ name: string; rows: ExportRow[] }>,
  filename: string
) {
  const workbook = XLSX.utils.book_new();

  sheets.forEach(({ name, rows }) => {
    const worksheet = XLSX.utils.json_to_sheet(
      rows.length > 0 ? rows : [{ Status: 'No data available' }]
    );
    worksheet['!cols'] = Object.keys(rows[0] ?? { Status: '' }).map((key) => ({
      wch: Math.max(key.length + 2, 18),
    }));
    XLSX.utils.book_append_sheet(workbook, worksheet, name);
  });

  XLSX.writeFile(workbook, filename);
}

function ChartCard({
  title,
  children,
  className = '',
  onExport,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
  onExport?: () => void;
}) {
  const { theme } = useTheme();

  return (
    <section
      className={`self-start rounded-xl border p-5 shadow-sm transition-shadow hover:shadow-md ${className}`}
      style={{
        borderColor: theme.border,
        backgroundColor: theme.surface,
      }}
    >
      <div
        className="mb-5 flex items-center justify-between gap-3 border-b pb-3"
        style={{ borderColor: theme.border }}
      >
        <h2 className="text-lg font-semibold" style={{ color: theme.text }}>
          {title}
        </h2>
        {onExport && (
          <button
            type="button"
            onClick={onExport}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition hover:bg-slate-50"
            style={{ borderColor: theme.border, color: theme.primary }}
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            Export
          </button>
        )}
      </div>

      <div>{children}</div>
    </section>
  );
}

function ChartSkeleton() {
  return (
    <div className="h-[300px] animate-pulse rounded-lg bg-gray-100">
      <div className="flex h-full items-end gap-3 px-6 pb-6">
        {[48, 72, 40, 88, 56, 68].map((height, index) => (
          <div
            key={index}
            className="flex-1 rounded-t bg-gray-200"
            style={{ height: `${height}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="flex h-[300px] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 text-center text-sm text-gray-500">
      No report data available.
    </div>
  );
}

export default function ReportsPage() {
  const { theme } = useTheme();
  const [draftFilters, setDraftFilters] =
    useState<ReportsFiltersState>(emptyFilters);
  const [filters, setFilters] = useState<ReportsFiltersState>(emptyFilters);
  const [selectedAvailabilityItem, setSelectedAvailabilityItem] = useState<
    AvailabilityReportItem | undefined
  >();

  const reportsQuery = useQuery<ReportsData>({
    queryKey: ['reports', filters],
    queryFn: async () => {
      const [
        skillsDistribution,
        availability,
        experienceDistribution,
        groupStatistics,
      ] = await Promise.all([
        getSkillsDistribution(filters),
        getAvailabilityReport(filters),
        getExperienceDistribution(filters),
        getGroupStatistics(filters),
      ]);

      return {
        skillsDistribution,
        availability,
        experienceDistribution,
        groupStatistics,
      };
    },
    ...freshQueryOptions,
  });

  const data = reportsQuery.data;

  const summaryQuery = useQuery<DashboardSummary>({
    queryKey: ['reports-dashboard-summary'],
    queryFn: getDashboardSummary,
    ...freshQueryOptions,
  });

  const summary = useMemo(() => {
    const dashboard = summaryQuery.data;
    return {
      totalUsers: dashboard?.totalUsers ?? 0,
      totalSkills: dashboard?.totalSkills ?? 0,
      activeGroups: dashboard?.totalGroups ?? 0,
      availableExperts: dashboard?.availableExperts ?? 0,
    };
  }, [summaryQuery.data]);

  const selectedAvailability =
    selectedAvailabilityItem ?? data?.availability[0];

  const exportSkills = () => {
    if (!data) return;
    downloadExcel(
      [
        {
          name: 'Skills',
          rows: data.skillsDistribution.map((item) => ({
            Skill: item.skillName,
            'Experts assigned': item.userCount,
          })),
        },
      ],
      'skill-distribution.xlsx'
    );
  };

  const exportAvailability = () => {
    if (!data) return;
    downloadExcel(
      [
        {
          name: 'Availability',
          rows: data.availability.map((item) => ({
            'Availability type':
              item.availabilityTypeName ||
              item.availabilityType ||
              'Availability',
            Experts: item.userCount ?? item.count ?? 0,
            'Total available hours': item.totalAvailableHours ?? 0,
            'Average available hours': item.averageAvailableHours ?? 0,
          })),
        },
      ],
      'availability-overview.xlsx'
    );
  };

  const exportExperience = () => {
    if (!data) return;
    downloadExcel(
      [
        {
          name: 'Experience',
          rows: data.experienceDistribution.map((item) => ({
            Range: item.range,
            Experts: item.count,
          })),
        },
      ],
      'experience-distribution.xlsx'
    );
  };

  const exportGroups = () => {
    if (!data) return;
    downloadExcel(
      [
        {
          name: 'Group statistics',
          rows: data.groupStatistics.map((item) => ({
            'Group type': item.groupType,
            Count: item.count,
          })),
        },
      ],
      'group-statistics.xlsx'
    );
  };

  const exportAllReports = () => {
    if (!data) return;
    downloadExcel(
      [
        {
          name: 'Summary',
          rows: [
            { Metric: 'Total users', Value: summary.totalUsers },
            { Metric: 'Total skills', Value: summary.totalSkills },
            { Metric: 'Active groups', Value: summary.activeGroups },
            { Metric: 'Available experts', Value: summary.availableExperts },
          ],
        },
        {
          name: 'Skills',
          rows: data.skillsDistribution.map((item) => ({
            Skill: item.skillName,
            'Experts assigned': item.userCount,
          })),
        },
        {
          name: 'Availability',
          rows: data.availability.map((item) => ({
            'Availability type':
              item.availabilityTypeName ||
              item.availabilityType ||
              'Availability',
            Experts: item.userCount ?? item.count ?? 0,
            'Total available hours': item.totalAvailableHours ?? 0,
            'Average available hours': item.averageAvailableHours ?? 0,
          })),
        },
        {
          name: 'Experience',
          rows: data.experienceDistribution.map((item) => ({
            Range: item.range,
            Experts: item.count,
          })),
        },
        {
          name: 'Group statistics',
          rows: data.groupStatistics.map((item) => ({
            'Group type': item.groupType,
            Count: item.count,
          })),
        },
      ],
      'reports-export.xlsx'
    );
  };

  const handleReset = () => {
    setDraftFilters(emptyFilters);
    setFilters(emptyFilters);
  };

  const labels = useMenuLabels();
  const reportsExpertsLabel = labels.reports;

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-6 px-2 pb-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1
            className="text-2xl font-bold md:text-3xl"
            style={{ color: theme.text }}
          >
            {reportsExpertsLabel}
          </h1>

          <p
            className="text-sm md:text-base"
            style={{ color: theme.textSecondary }}
          >
            Enterprise workforce insights and skill analytics
          </p>
        </div>
        <button
          type="button"
          onClick={exportAllReports}
          disabled={!data}
          className="inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          style={{ backgroundColor: theme.primary, color: theme.contrastText }}
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          Export all
        </button>
      </header>

      {/* <ReportsFilters
        draftFilters={draftFilters}
        onDraftChange={setDraftFilters}
        onApply={() => setFilters(draftFilters)}
        onReset={handleReset}
      /> */}

      {reportsQuery.isLoading ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={index}
                className="h-28 animate-pulse rounded-lg bg-gray-100"
              />
            ))}
          </div>
          <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
            {Array.from({ length: 5 }).map((_, index) => (
              <ChartSkeleton key={index} />
            ))}
          </div>
        </>
      ) : reportsQuery.isError ? (
        <div
          className="flex min-h-[320px] flex-col items-center justify-center rounded-lg border bg-white px-6 py-10 text-center shadow-sm"
          style={{ borderColor: theme.border }}
        >
          <h2 className="mb-2 text-xl font-bold" style={{ color: theme.text }}>
            Unable to load reports.
          </h2>
          <p className="mb-0 text-sm" style={{ color: theme.textSecondary }}>
            Please try again.
          </p>
          <button
            type="button"
            onClick={() => reportsQuery.refetch()}
            className="mt-4 inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
            style={{
              backgroundColor: theme.primary,
              color: theme.contrastText,
            }}
          >
            Retry
          </button>
        </div>
      ) : data ? (
        <>
          <SummaryCards {...summary} />

          <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
            <ChartCard
              title="Skill Distribution"
              className="xl:col-span-2"
              onExport={exportSkills}
            >
              {data.skillsDistribution.length > 0 ? (
                <SkillDistributionChart data={data.skillsDistribution} />
              ) : (
                <EmptyChart />
              )}
            </ChartCard>

            <ChartCard
              title="Availability Overview"
              className="xl:order-first xl:col-span-2"
              onExport={exportAvailability}
            >
              {data.availability.length > 0 ? (
                <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)] lg:items-center">
                  <div className="mx-auto w-full max-w-[280px]">
                    <AvailabilityChart data={data.availability} />
                  </div>

                  <AvailabilitySummaryCards
                    data={data.availability}
                    selectedTypeId={
                      selectedAvailability?.availabilityTypeId ??
                      data.availability.indexOf(
                        selectedAvailability as AvailabilityReportItem
                      )
                    }
                    onSelect={setSelectedAvailabilityItem}
                  />
                </div>
              ) : (
                <EmptyChart />
              )}
            </ChartCard>

            <ChartCard
              title="Experience Distribution"
              onExport={exportExperience}
            >
              {data.experienceDistribution.length > 0 ? (
                <ExperienceChart data={data.experienceDistribution} />
              ) : (
                <EmptyChart />
              )}
            </ChartCard>

            <ChartCard title="Created Group Statistics" onExport={exportGroups}>
              {data.groupStatistics.length > 0 ? (
                <GroupStatisticsChart data={data.groupStatistics} />
              ) : (
                <EmptyChart />
              )}
            </ChartCard>
          </div>
        </>
      ) : null}
    </div>
  );
}
